import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { isCriticalActionActive } from "@/lib/criticalAction";

const INTERVAL_MS = 5 * 60 * 1000;
const RELOADED_KEY = "version-reloaded-to";
const CURRENT = __APP_BUILD_ID__;

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return null;
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.version === "string" ? data.version : null;
  } catch {
    return null; // offline / rede: silencioso
  }
}

export function useVersionCheck() {
  const location = useLocation();
  const pendingRef = useRef<string | null>(null);
  const toastShownRef = useRef(false);
  const checkingRef = useRef(false);

  const tryReload = (remote: string) => {
    try {
      // Anti-loop: já recarregamos para essa versão e o JS continua antigo (CDN)
      if (sessionStorage.getItem(RELOADED_KEY) === remote) return;
    } catch { /* noop */ }

    if (isCriticalActionActive()) {
      pendingRef.current = remote;
      if (!toastShownRef.current) {
        toastShownRef.current = true;
        toast("Nova versão disponível", {
          description: "Salve seu trabalho e clique aqui para atualizar.",
          duration: Infinity,
          action: { label: "Atualizar", onClick: () => window.location.reload() },
        });
      }
      return;
    }
    try { sessionStorage.setItem(RELOADED_KEY, remote); } catch { /* noop */ }
    window.location.reload();
  };

  const check = async () => {
    if (import.meta.env.DEV || checkingRef.current) return;
    if (pendingRef.current) { tryReload(pendingRef.current); return; }
    checkingRef.current = true;
    try {
      const remote = await fetchRemoteVersion();
      if (remote && remote !== CURRENT) tryReload(remote);
    } finally {
      checkingRef.current = false;
    }
  };

  useEffect(() => {
    if (import.meta.env.DEV) return;
    const id = window.setInterval(check, INTERVAL_MS);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
}
